import React, { useContext, useEffect, useState } from 'react';
import { YourWorkItemsInputStatus } from '@99designs/graph-utils/types';
import { useAppConfig } from '@99designs/layout';
import { trackEvent } from '@99designs/tracking';
import { useCompletedWorkItemsQuery } from './Payments.generated';

interface Props {
    children: React.ReactNode;
    locale: string;
}

export const paymentsContext = React.createContext<Payments | undefined>(undefined);

interface PaymentModal {
    isAvailable: boolean;
    asyncShowPrepare: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    asyncShowFinish: (paymentInput: any) => void;
}

export interface Payments {
    paymentModal?: PaymentModal;
}

declare global {
    interface Window {
        payments?: Payments;
    }
}

// TODO: investigate error modes for when the payments modal is unavailable
// Trying to use the payments modal via this hook when it's unavailble
// should probably error.
// See https://gitlab.com/vistaprint-org/99designs/frontend/-/merge_requests/1949#discussion_r478815669
// for discussion
export function usePayments() {
    return useContext(paymentsContext);
}

enum CustomerStatus {
    NEW = 'New',
    RETURNING = 'Returning',
}

export default function PaymentsProvider({ children, locale }: Props) {
    const appConfig = useAppConfig();

    if (typeof window === 'undefined') {
        return (
            <paymentsContext.Provider value={{ paymentModal: undefined }}>
                {children}
            </paymentsContext.Provider>
        );
    }

    // FIXME: Rule of hooks violation https://gitlab.com/vistaprint-org/99designs/frontend/-/issues/3523
    /* eslint-disable react-hooks/rules-of-hooks */
    const [payments, setPayments] = useState(window.payments);

    const { data } = useCompletedWorkItemsQuery({
        variables: {
            input: {
                status: 'COMPLETED' as YourWorkItemsInputStatus,
                page: 1,
            },
        },
    });

    const customerStatus =
        (data?.yourWorkItems?.completedCount ?? 0) > 0
            ? CustomerStatus.RETURNING
            : CustomerStatus.NEW;

    useEffect(() => {
        const span = document.createElement('span');
        span.id = 'payment-span';
        span.setAttribute('data-payment-apikey', appConfig.paymentsApiKey || '');
        span.setAttribute(
            'data-payment-modal_iframe_url',
            appConfig.paymentsApiModalIframeUrl || ''
        );
        span.setAttribute('data-payment-locale', locale);

        const paymentsSpan = document.getElementById('payment-span');
        if (!paymentsSpan) {
            document.getElementsByTagName('body')[0].appendChild(span);
        } else {
            paymentsSpan.replaceWith(span);
        }

        const script = document.createElement('script');
        script.id = 'payment-script';
        script.src = appConfig.paymentsScriptUrl || '';
        script.async = true;
        script.onload = () => {
            if (window.payments) {
                setPayments(window.payments);
            }
        };

        const paymentsScript = document.getElementById('payment-script');
        if (!paymentsScript) {
            document.getElementsByTagName('body')[0].appendChild(script);
        } else {
            paymentsScript.replaceWith(script);
        }

        return () => {
            const paymentSpan = document.getElementById('payment-span');
            if (paymentSpan) {
                paymentSpan.remove();
            }

            const paymentScript = document.getElementById('payment-script');
            if (paymentScript) {
                paymentScript.remove();
                window.payments = undefined;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        appConfig.paymentsApiKey,
        appConfig.paymentsApiModalIframeUrl,
        appConfig.paymentsScriptUrl,
    ]);

    useEffect(() => {
        const paymentSuccess = (event: CustomEvent) => {
            const detail = event.detail;

            const items = detail?.payment?.purchase_details?.items;
            const signedMetadata = detail?.payment?.signed_metadata;

            let payload: Record<string, unknown> = {
                customer_status: customerStatus,
            };

            if (items) {
                const currency = detail?.payment?.currency;
                payload = {
                    ...payload,
                    currency,
                    ...calculatePayloadFromPaymentItemsAndMetadata(items, signedMetadata, currency),
                };
            }

            trackEvent('Project brief payment successful', payload);
        };

        type Item = {
            amount: string;
            liability: string;
            product_key?: string;
        };

        const calculatePayloadFromPaymentItemsAndMetadata = (
            items: Item[],
            signed_metadata: string,
            currency: string
        ) => {
            let exchangeRate: number;
            let orderId: string | undefined;

            try {
                const meta = JSON.parse(signed_metadata);
                exchangeRate = parseFloat(meta?.exchange_rate);
                orderId = meta?.order_data?.order_key;
            } catch (err) {
                exchangeRate = 1.0;
            }

            const profit = items.reduce((acc: number, item: Item) => {
                const amount = parseInt(item.amount);
                const liability = parseInt(item.liability);

                return acc + (amount - liability);
            }, 0);

            const productKeys = items.map((item) => item.product_key);

            const usdProfitInCents = Math.round(profit * exchangeRate);

            const revenue = getRevenue(profit, currency);

            return {
                exchangeRate,
                usdProfitInCents,
                revenue,
                orderId,
                productKeys,
            };
        };

        const getRevenue = (profit: number, currency: string) => {
            if (currency === 'JPY') {
                return profit;
            }

            return parseFloat((profit / 100).toFixed(2));
        };
        window.addEventListener('Payments.vendor.paymentSuccess', paymentSuccess);

        return () => {
            window.removeEventListener('Payments.vendor.paymentSuccess', paymentSuccess);
        };
    }, [customerStatus]);

    // This can be removed when related eslint-disable rule is fixed
    /* eslint-enable react-hooks/rules-of-hooks */

    return (
        <paymentsContext.Provider
            value={{
                paymentModal: payments ? payments.paymentModal : undefined,
            }}
        >
            {children}
        </paymentsContext.Provider>
    );
}
